if (!customElements.get('brp-filter-shell')) {
  customElements.define('brp-filter-shell', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.shell = this.querySelector('[data-filter-shell]');
      this.desktop = matchMedia('(min-width: 750px)');
      this.syncShell = () => { this.shell.open = this.desktop.matches; };
      this.syncShell();
      this.desktop.addEventListener('change', this.syncShell);
      this.form = this.querySelector('form');
      this.submitHandler = event => {
        const controller = this.querySelector('facet-filters-form');
        if (typeof controller?.onSubmitHandler === 'function') controller.onSubmitHandler(event);
      };
      this.form.addEventListener('submit', this.submitHandler);
    }
    disconnectedCallback() {
      this.desktop?.removeEventListener('change', this.syncShell);
      this.form?.removeEventListener('submit', this.submitHandler);
      this.initialized = false;
    }
  });
}

if (!customElements.get('brp-vehicle-facets')) {
  customElements.define('brp-vehicle-facets', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.initialize().catch(() => {
        if (!this.isConnected) return;
        this.querySelector('[data-vehicle-error]').hidden = false;
        this.querySelector('fieldset').disabled = true;
      });
    }
    async initialize() {
      if (!window.brpSidebarVehicleData) {
        window.brpSidebarVehicleData = fetch(this.dataset.source).then(response => {
          if (!response.ok) throw new Error('Vehicle data unavailable');
          return response.json();
        }).catch(error => { delete window.brpSidebarVehicleData; throw error; });
      }
      const vehicles = await window.brpSidebarVehicleData;
      this.fixedMake = this.dataset.fixedMake || '';
      this.vehicles = this.fixedMake ? vehicles.filter(vehicle => vehicle.make === this.fixedMake) : vehicles;
      if (!this.isConnected) return;
      this.year = this.querySelector('[data-vehicle-year]');
      this.make = this.querySelector('[data-vehicle-make]');
      this.model = this.querySelector('[data-vehicle-model]');
      this.tags = this.querySelector('[data-vehicle-tags]');
      const params = new URLSearchParams(location.search);
      this.otherTags = this.tags.value.split(',').filter(tag => tag && !tag.startsWith('fit-'));
      this.fill(this.year, [...new Set(this.vehicles.map(v => String(v.year)))].sort((a, b) => Number(b) - Number(a)));
      this.year.value = params.get('brp_year') || '';
      this.refreshMakes();
      this.make.value = this.fixedMake || params.get('brp_make') || '';
      this.refreshModels();
      this.model.value = params.get('brp_model') || '';
      this.updateTags();
      this.year.addEventListener('change', () => { this.refreshMakes(); this.updateTags(); });
      this.make.addEventListener('change', () => { this.refreshModels(); this.updateTags(); });
      this.model.addEventListener('change', () => this.updateTags());
      this.querySelector('[data-reset-vehicle]').addEventListener('click', () => {
        this.year.value = '';
        this.refreshMakes();
        this.updateTags();
        this.tags.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }
    fill(select, values) {
      select.replaceChildren(new Option(this.dataset.any, ''));
      values.forEach(value => select.add(new Option(value, value)));
      select.disabled = values.length === 0;
    }
    refreshMakes() {
      if (this.fixedMake) {
        this.fill(this.make, [this.fixedMake]);
        this.make.value = this.fixedMake;
        this.refreshModels();
        return;
      }
      const makes = this.year.value ? this.vehicles.filter(v => String(v.year) === this.year.value).map(v => v.make) : [];
      this.fill(this.make, [...new Set(makes)].sort((a, b) => a.localeCompare(b)));
      this.refreshModels();
    }
    refreshModels() {
      const models = this.make.value ? this.vehicles.filter(v => String(v.year) === this.year.value && v.make === this.make.value).map(v => v.model) : [];
      this.fill(this.model, [...new Set(models)].sort((a, b) => a.localeCompare(b)));
    }
    updateTags() {
      const matches = this.year.value ? this.vehicles.filter(v =>
        String(v.year) === this.year.value &&
        (!this.make.value || v.make === this.make.value) &&
        (!this.model.value || v.model === this.model.value)
      ) : [];
      const tags = new Set(this.otherTags);
      matches.forEach(vehicle => {
        tags.add(vehicle.tag);
        (vehicle.drivetrains || []).forEach(drive => {
          tags.add(vehicle.tag + '-' + drive);
          (vehicle.additionalTags?.[drive] || []).forEach(tag => tags.add(tag));
        });
      });
      this.tags.value = [...tags].join(',');
      this.querySelector('[data-vehicle-tag-fields]').replaceChildren(...[...tags].map(tag => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'filter.p.tag';
        input.value = tag;
        return input;
      }));
    }
  });
}

if (!customElements.get('brp-price-filter')) {
  customElements.define('brp-price-filter', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.minimum = this.querySelector('[data-price-min]');
      this.maximum = this.querySelector('[data-price-max]');
      this.lowRange = this.querySelector('[data-price-range-min]');
      this.highRange = this.querySelector('[data-price-range-max]');
      this.limit = Number(this.dataset.max);
      this.lowRange.addEventListener('input', () => this.moveRange(true));
      this.highRange.addEventListener('input', () => this.moveRange(false));
      [this.minimum, this.maximum].forEach(input => {
        input.addEventListener('input', () => this.syncRanges());
        input.addEventListener('change', () => {
          if (input.value !== '') {
            input.value = String(Math.max(0, Math.min(this.limit, Number(input.value) || 0)));
            if (this.minimum.value !== '' && this.maximum.value !== '' && Number(this.minimum.value) > Number(this.maximum.value)) {
              if (input === this.minimum) input.value = this.maximum.value;
              else input.value = this.minimum.value;
            }
          }
          this.syncRanges();
        });
      });
      this.syncRanges();
    }
    moveRange(isMinimum) {
      const low = Number(this.lowRange.value);
      const high = Number(this.highRange.value);
      if (isMinimum) {
        this.minimum.value = String(Math.min(low, high));
        this.lowRange.value = this.minimum.value;
        this.minimum.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        this.maximum.value = String(Math.max(low, high));
        this.highRange.value = this.maximum.value;
        this.maximum.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.paint();
    }
    syncRanges() {
      const low = this.minimum.value === '' ? 0 : Number(this.minimum.value);
      const high = this.maximum.value === '' ? this.limit : Number(this.maximum.value);
      this.lowRange.value = String(Math.max(0, Math.min(this.limit, low)));
      this.highRange.value = String(Math.max(low, Math.min(this.limit, high)));
      this.paint();
    }
    paint() {
      const limit = this.limit || 1;
      this.style.setProperty('--price-low', (Number(this.lowRange.value) / limit * 100) + '%');
      this.style.setProperty('--price-high', (Number(this.highRange.value) / limit * 100) + '%');
      this.lowRange.style.zIndex = Number(this.lowRange.value) >= limit * .95 ? '3' : '2';
    }
  });
}

if (!customElements.get('brp-spec-facets')) {
  customElements.define('brp-spec-facets', class extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-spec-filter]').forEach(select => {
        const selected = select.value;
        const choices = [...select.options].slice(1);
        choices.sort((a, b) => {
          if (select.hasAttribute('data-numeric-values')) {
            const left = Number.parseFloat(a.value);
            const right = Number.parseFloat(b.value);
            if (Number.isFinite(left) && Number.isFinite(right) && left !== right) return left - right;
          }
          return a.text.localeCompare(b.text, undefined, { numeric: true, sensitivity: 'base' });
        });
        choices.forEach(option => select.add(option));
        select.value = selected;
      });
    }
  });
}
